import { RecurringIcon } from 'app/_authenticated/trade/components/CurrencyIcon/RecurringIcon';
import { GradientOrColorView } from 'components/GradientOrColorView/GradientOrColorView';
import { useProduct } from 'hooks/products/useProducts';
import { useAppTheme } from 'hooks/useAppTheme';
import { useMemo } from 'react';
import { View } from 'react-native';
import { ReactSVG } from 'react-svg';
import appUrls from 'utils/app-urls';

type BottomRightIcon = 'RECURRING_BUY';

export const CurrencyIcon = ({
  code,
  isVaultProduct = false,
  isLockedAsset = false,
  size = 32,
  style,
  iconColor = '#FFFFFF',
  transparent = false,
  padding = 1,
  bottomRightIcon,
  cornerIconSize = 16,
  cornerIconBackground,
  invertedColors = false,
  className,
}: {
  code?: string;
  isVaultProduct?: boolean;
  isLockedAsset?: boolean;
  /** The `width` and `height` of the icon. Default is `32`*/
  size?: number;
  style?: any;
  iconColor?: string;
  transparent?: boolean;
  padding?: number;
  bottomRightIcon?: BottomRightIcon;
  cornerIconSize?: number;
  cornerIconBackground?: string;
  invertedColors?: boolean;
  className?: string;
}) => {
  if (isVaultProduct) {
    code = code?.replace('v', '');
  }

  const { product: currency } = useProduct(code);
  const color = useProductColor(code, isLockedAsset, isVaultProduct);
  const primaryColor = useProductPrimaryColor(code, isLockedAsset);
  const hasInvertedIcon =
    currency?.type === 'Bundle' ||
    currency?.type === 'Crypto' ||
    currency?.type === 'Stablecoin' ||
    currency?.type === 'Stock';

  const containerBgColor = transparent ? 'transparent' : (color ?? iconColor);
  const containerStyle = {
    position: 'relative',
    borderRadius: (size + padding * 2) / 2,
    width: size,
    height: size,
    ...style,
  };

  const bottomRightIconStyle = {
    right: -cornerIconSize / 6,
    bottom: -cornerIconSize / 6,
    position: 'absolute',
    margin: 'auto',
  } as const;

  return (
    <GradientOrColorView
      style={containerStyle}
      colorOrGradient={invertedColors ? '#FFFFFF' : containerBgColor}
      className={className ?? ''}>
      {code != null ? (
        <CDNIcon
          code={code}
          size={size}
          invertedColors={invertedColors && hasInvertedIcon}
        />
      ) : (
        <></>
      )}
      {bottomRightIcon && (
        <View style={bottomRightIconStyle}>
          {getBottomRightIcon(
            bottomRightIcon,
            cornerIconSize,
            invertedColors ? '#FFFFFF' : primaryColor,
            cornerIconBackground,
          )}
        </View>
      )}
    </GradientOrColorView>
  );
};

function useProductColor(
  code?: string,
  isLocked: boolean = false,
  isVault: boolean = false,
) {
  const theme = useAppTheme();
  const { product } = useProduct(code);

  return useMemo(() => {
    if (code == null) return null;

    const productColors = product?.colors;

    const productColor = isLocked
      ? '#465480'
      : product?.type === 'Fiat'
        ? 'transparent'
        : isVault
          ? productColors?.vault
          : productColors?.primaryStart && productColors.primaryEnd
            ? {
                start: productColors?.primaryStart,
                end: productColors.primaryEnd,
              }
            : productColors?.primary;

    return productColor || '#626AFF';
  }, [code, isLocked, isVault, theme, product]);
}

function useProductPrimaryColor(code?: string, isLocked: boolean = false) {
  const { product } = useProduct(code);

  return useMemo(() => {
    if (code == null) return '#626AFF';

    const productColors = product?.colors;

    const productColor = isLocked
      ? '#465480'
      : productColors?.primaryStart && productColors.primaryEnd
        ? productColors?.primaryStart
        : productColors?.primary;

    return productColor || '#626AFF';
  }, [code, product]);
}

const getBottomRightIcon = (
  bottomRightCode: BottomRightIcon,
  size: number,
  iconColor: string,
  cornerIconBackground?: string,
) => {
  switch (bottomRightCode) {
    case 'RECURRING_BUY':
      return (
        <RecurringIcon
          width={size}
          height={size}
          stroke={iconColor}
          fill={cornerIconBackground ?? iconColor}
        />
      );
    default:
      return <></>;
  }
};

const CDNIcon = ({
  code,
  size,
  invertedColors = false,
}: {
  code: string;
  size: number;
  invertedColors?: boolean;
}) => {
  const cdnUrl = appUrls.getCDNUrl();
  const uri = `${cdnUrl}assets/products/icons/${invertedColors ? 'inverted/' : ''}${code?.toLowerCase?.()}.svg`;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size,
        overflow: 'hidden',
      }}>
      <ReactSVG
        src={uri}
        beforeInjection={svg => {
          const sizeCorrected = size + (size > 60 ? 2 : 0);
          svg.setAttribute('width', sizeCorrected.toString());
          svg.setAttribute('height', sizeCorrected.toString());
        }}
      />
    </div>
  );
};
export default CurrencyIcon;
